import { useEffect, useRef } from "react";

type UseNewVersionOpts = {
  intervalMs?: number;
  url?: string; // endpoint that returns version (string or JSON { version })
  onUpdate: () => void;
};

export default function useNewVersion({
  intervalMs = 2000,
  url = "/version.json",
  onUpdate,
}: UseNewVersionOpts) {
  const latestRef = useRef<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const bcRef = useRef<BroadcastChannel | null>(null);

  // parse fetch response into a version string (support plain text or { version })
  const parseVersion = async (res: Response) => {
    const txt = await res.text();
    try {
      const j = JSON.parse(txt);
      if (j && typeof j.version === "string") return j.version;
    } catch {
      /* not json */
    }
    return txt.trim();
  };

  useEffect(() => {
    let mounted = true;

    const postUpdate = (version: string) => {
      try {
        // broadcast to other same-origin tabs
        if (bcRef.current) {
          bcRef.current.postMessage({ version });
        }
        // storage fallback (still same-origin)
        const storageValue = `${version}|${Date.now()}`;
        localStorage.setItem("msi-app-version", storageValue);
      } catch (error) {
        console.error('Error in postUpdate:', error);
      }
      onUpdate();
    };

    const handleRemote = (version: string) => {
      if (!mounted) return;
      if (!latestRef.current) {
        latestRef.current = version;
        return;
      }
      if (version !== latestRef.current) {
        latestRef.current = version;
        postUpdate(version);
      }
    };

    const checkOnce = async () => {
      try {
        const res = await fetch(url, { cache: "no-store", credentials: "same-origin" });
        if (!res.ok) {
          console.warn(`Version check failed: ${res.status} ${res.statusText}`);
          return;
        }
        
        const version = await parseVersion(res);
        
        if (!version) {
          console.warn('No version found in response');
          return;
        }

        // Always check against stored version to detect updates that happened while away
        let shouldUpdate = false;
        let storedVersion = null;
        
        try {
          const stored = localStorage.getItem("msi-app-version");
          if (stored) {
            [storedVersion] = stored.split("|");
          }
        } catch (error) {
          console.log('Error reading stored version:', error);
        }

        if (!latestRef.current) {
          // Initial load - check if current fetched version is different from stored
          
          if (storedVersion && storedVersion !== version) {
            console.log('New version detected on initial load!');
            shouldUpdate = true;
          }
          
          latestRef.current = version;
        } else {
          // Regular check - compare with our current ref
          if (version !== latestRef.current) {
            console.log('Version changed during runtime!', { from: latestRef.current, to: version });
            latestRef.current = version;
            shouldUpdate = true;
          }
        }

        if (shouldUpdate) {
          postUpdate(version);
        }

        if (version !== latestRef.current) {
          console.log('Version changed!', { from: latestRef.current, to: version });
          latestRef.current = version;
          postUpdate(version);
        } 
      } catch (error) {
        console.error('Version check error:', error);
      }
    };

    // BroadcastChannel to notify other tabs/windows (same-origin)
    try {
      const bc = new BroadcastChannel("msi-app-version");
      bc.onmessage = (ev) => {
        const v = ev.data?.version;
        if (typeof v === "string") handleRemote(v);
      };
      bcRef.current = bc;
    } catch {
      bcRef.current = null;
    }

    // storage event fallback (same-origin)
    const onStorage = (e: StorageEvent) => {
      if (e.key === "msi-app-version" && typeof e.newValue === "string") {
        const [version] = e.newValue.split("|");
        if (version) handleRemote(version);
      }
    };
    window.addEventListener("storage", onStorage);

    // visibility / focus hooks -> immediate check when user returns or first load
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkOnce();
    };
    const onFocus = () => checkOnce();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);


    
    // initial immediate check
    checkOnce();

    // polling interval
    intervalRef.current = window.setInterval(checkOnce, intervalMs);

    return () => {
      mounted = false;
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      if (bcRef.current) {
        bcRef.current.close();
        bcRef.current = null;
      }
    };
  }, [intervalMs, url, onUpdate]);
}